import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { ShieldWarning, Lock, Eye, EyeSlash, ArrowsClockwise, Warning, CheckCircle, Fingerprint } from "@phosphor-icons/react";
import { toast } from "sonner";

const ACTION_LABELS = {
  "login.success": { label: "Login", color: "#008A00" },
  "login.failed": { label: "Login failed", color: "#E00000" },
  "login.locked": { label: "Login LOCKED", color: "#E00000" },
  "login.disabled": { label: "Disabled-account login", color: "#FF9800" },
  "logout": { label: "Logout", color: "#52525B" },
  "leads.export": { label: "Lead export", color: "#FF9800" },
  "lead.delete": { label: "Lead deleted", color: "#E00000" },
  "leads.bulk_delete": { label: "Bulk delete", color: "#E00000" },
  "webhook_secret.revealed": { label: "Webhook secret revealed", color: "#FF9800" },
  "mcp_token.revealed": { label: "MCP token revealed", color: "#FF9800" },
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

export default function Security() {
  const [summary, setSummary] = useState(null);
  const [events, setEvents] = useState([]);
  const [backups, setBackups] = useState(null);
  const [backupRunning, setBackupRunning] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ action: "", email: "", days: 7 });
  const [decodeInput, setDecodeInput] = useState("");
  const [decodeResult, setDecodeResult] = useState(null);
  const [decoding, setDecoding] = useState(false);

  const runBackupNow = async () => {
    setBackupRunning(true);
    try {
      const { data } = await api.post("/security/backups/run-now");
      if (data.status === "success") {
        toast.success(`Backup completed — ${(data.size_bytes / 1024).toFixed(0)} KB in ${data.duration_ms}ms`);
      } else {
        toast.error(`Backup failed: ${data.error || "unknown"}`);
      }
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBackupRunning(false);
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = { days: filter.days, limit: 500 };
      if (filter.action) params.action = filter.action;
      if (filter.email) params.actor_email = filter.email;
      const [s, a, b] = await Promise.all([
        api.get("/security/summary"),
        api.get("/security/audit", { params }),
        api.get("/security/backups").catch(() => ({ data: null })),
      ]);
      setSummary(s.data);
      setEvents(a.data.events);
      setBackups(b.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filter]);

  const decode = async () => {
    if (!decodeInput.trim()) { toast.error("Paste the suspect text first"); return; }
    setDecoding(true);
    try {
      const { data } = await api.post("/security/decode-watermark", { text: decodeInput });
      setDecodeResult(data);
      if (data.found && data.exporter) {
        toast.success(`Watermark identified — exported by ${data.exporter.email}`);
      } else if (data.found) {
        toast.success("Watermark decoded (no audit match)");
      } else {
        toast.error("No watermark detected");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setDecoding(false);
  };

  return (
    <div className="min-h-screen">
      <PageHeader
        eyebrow="Admin"
        title="Security"
        description="Brute-force defence, audit trail and suspicious activity — review who did what, from where, and when."
        actions={
          <button onClick={load} data-testid="security-refresh"
            className="px-4 py-2.5 text-sm font-semibold border border-border hover:bg-secondary rounded-sm flex items-center gap-2">
            <ArrowsClockwise size={16} /> Refresh
          </button>
        }
      />

      {/* Summary cards */}
      <div className="px-8 pt-6 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="security-summary">
        <Stat label="Failed logins · 24h" value={summary?.failed_logins_24h ?? 0}
          color={(summary?.failed_logins_24h ?? 0) > 20 ? "#E00000" : "#52525B"}
          icon={Warning} testid="stat-failed" />
        <Stat label="Locked accounts · 24h" value={summary?.locked_buckets_24h ?? 0}
          color={(summary?.locked_buckets_24h ?? 0) > 0 ? "#E00000" : "#008A00"}
          icon={Lock} testid="stat-locked" />
        <Stat label="Lead exports · 7d" value={summary?.exports_7d ?? 0}
          color={(summary?.exports_7d ?? 0) > 10 ? "#FF9800" : "#52525B"}
          icon={Eye} testid="stat-exports" />
        <Stat label="Deletes · 7d" value={summary?.deletes_7d ?? 0}
          color={(summary?.deletes_7d ?? 0) > 0 ? "#FF9800" : "#52525B"}
          icon={ShieldWarning} testid="stat-deletes" />
        <Stat label="Phone reveals · 24h" value={summary?.phone_reveals_24h ?? 0}
          color={(summary?.phone_reveals_24h ?? 0) > 20 ? "#FF9800" : "#52525B"}
          icon={Eye} testid="stat-reveals" />
        <Stat label="Foreign-IP blocks · 24h" value={summary?.foreign_ip_blocks_24h ?? 0}
          color={(summary?.foreign_ip_blocks_24h ?? 0) > 0 ? "#E00000" : "#008A00"}
          icon={ShieldWarning} testid="stat-foreign" />
        <Stat label="Off-hours events · 24h" value={summary?.off_hours_events_24h ?? 0}
          color={(summary?.off_hours_events_24h ?? 0) > 20 ? "#FF9800" : "#52525B"}
          icon={Warning} testid="stat-offhours" />
        <Stat label="Active sessions" value={summary?.active_sessions ?? 0}
          color="#002FA7" icon={Fingerprint} testid="stat-sessions" />
      </div>

      {/* Default-password nag banner */}
      {(summary?.default_password_users ?? 0) > 0 && (
        <div className="px-8 pt-4" data-testid="default-pw-banner">
          <div className="border border-amber-400 bg-amber-50 px-4 py-3 flex items-start gap-3">
            <Warning size={18} weight="fill" className="text-amber-700 shrink-0 mt-0.5" />
            <div className="text-xs text-amber-900">
              <div className="font-bold uppercase tracking-[0.15em] mb-0.5">
                {summary.default_password_users} account{summary.default_password_users > 1 ? "s" : ""} using a default / weak password
              </div>
              These users will be forced to rotate their password on next login. To force one now, disable the account or contact them directly.
            </div>
          </div>
        </div>
      )}

      {/* Top exporters + Top phone-revealers (side by side) */}
      {((summary?.top_exporters_7d?.length ?? 0) > 0 || (summary?.top_phone_revealers_24h?.length ?? 0) > 0) && (
        <div className="px-8 pt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          {(summary?.top_exporters_7d?.length ?? 0) > 0 && (
            <div className="bg-white border border-border" data-testid="top-exporters">
              <div className="px-4 py-2 border-b border-border">
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Data exfil watch</div>
                <div className="text-sm font-bold">Top exporters · last 7 days</div>
              </div>
              <table className="w-full text-sm">
                <tbody>
                  {summary.top_exporters_7d.map((r, i) => (
                    <tr key={i} className="border-t border-border">
                      <td className="px-4 py-2 text-xs">{r.email || "—"}</td>
                      <td className="px-4 py-2 text-right tabular-nums font-bold">{r.count}</td>
                      <td className="px-4 py-2 text-right text-[10px] text-muted-foreground">{fmtDate(r.last_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {(summary?.top_phone_revealers_24h?.length ?? 0) > 0 && (
            <div className="bg-white border border-border" data-testid="top-revealers">
              <div className="px-4 py-2 border-b border-border">
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">PII access watch</div>
                <div className="text-sm font-bold">Top phone revealers · last 24h</div>
              </div>
              <table className="w-full text-sm">
                <tbody>
                  {summary.top_phone_revealers_24h.map((r, i) => (
                    <tr key={i} className="border-t border-border">
                      <td className="px-4 py-2 text-xs">{r.email || "—"}</td>
                      <td className="px-4 py-2 text-right tabular-nums font-bold">{r.count}</td>
                      <td className="px-4 py-2 text-right text-[10px] text-muted-foreground">{fmtDate(r.last_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Backups panel */}
      {backups && (
        <div className="px-8 pt-6" data-testid="backups-panel">
          <div className="bg-white border border-border">
            <div className="px-4 py-3 border-b border-border flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Disaster recovery</div>
                <div className="text-sm font-bold flex items-center gap-2">
                  MongoDB backups
                  <span className={`text-[9px] uppercase tracking-[0.15em] font-bold px-2 py-0.5 border ${backups.config.s3_configured ? "border-[#008A00] text-[#008A00] bg-[#008A00]/5" : "border-amber-500 text-amber-700 bg-amber-50"}`}>
                    {backups.config.s3_configured ? "S3 sync ON" : "Local only"}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                  Daily · {backups.config.daily_at_utc} UTC · keep last {backups.config.retention_local}
                </span>
                <button
                  onClick={runBackupNow}
                  disabled={backupRunning}
                  data-testid="backup-run-now"
                  className="px-3 py-1.5 border border-primary bg-primary text-primary-foreground text-[10px] uppercase tracking-[0.15em] font-bold hover:bg-[#002277] disabled:opacity-40"
                >
                  {backupRunning ? "Running…" : "Backup now"}
                </button>
              </div>
            </div>
            {backups.local.length === 0 ? (
              <div className="px-4 py-6 text-center text-xs text-muted-foreground">
                No backups yet. Click <b>Backup now</b> to create the first one.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-secondary/40">
                  <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                    <th className="px-4 py-2 font-semibold">File</th>
                    <th className="px-4 py-2 font-semibold text-right">Size</th>
                    <th className="px-4 py-2 font-semibold text-right">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {backups.local.map((f) => (
                    <tr key={f.filename} className="border-t border-border">
                      <td className="px-4 py-2 font-mono text-xs">{f.filename}</td>
                      <td className="px-4 py-2 text-right tabular-nums text-xs">{(f.size_bytes / 1024).toFixed(0)} KB</td>
                      <td className="px-4 py-2 text-right text-[10px] text-muted-foreground">{fmtDate(f.modified_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Top suspicious */}
      {summary?.top_suspicious_24h?.length > 0 && (
        <div className="px-8 pt-6" data-testid="security-suspicious">
          <h2 className="font-display text-base font-bold tracking-tight mb-3 flex items-center gap-2">
            <Warning size={16} weight="bold" style={{ color: "#E00000" }} />
            Suspicious activity (last 24h)
          </h2>
          <div className="bg-white border border-border">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.15em] text-muted-foreground border-b border-border">
                  <th className="px-4 py-2 font-bold">IP</th>
                  <th className="px-4 py-2 font-bold">Targeted email</th>
                  <th className="px-4 py-2 font-bold text-right">Failed attempts</th>
                </tr>
              </thead>
              <tbody>
                {summary.top_suspicious_24h.map((s, i) => (
                  <tr key={i} className="border-b border-border last:border-0">
                    <td className="px-4 py-2 font-mono text-xs">{s.ip || "—"}</td>
                    <td className="px-4 py-2 text-xs">{s.email || "—"}</td>
                    <td className="px-4 py-2 text-right tabular-nums font-bold"
                      style={{ color: s.failed_attempts >= 5 ? "#E00000" : "#FF9800" }}>
                      {s.failed_attempts}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            <Lock size={12} className="inline mr-1" />
            Accounts auto-lock for {summary.thresholds.lockout_minutes} min after {summary.thresholds.max_failures} failed attempts per IP+email.
          </p>
        </div>
      )}

      {/* Watermark forensic decoder */}
      <div className="px-8 pt-6" data-testid="security-watermark">
        <h2 className="font-display text-base font-bold tracking-tight mb-3 flex items-center gap-2">
          <Fingerprint size={16} weight="bold" style={{ color: "#002FA7" }} />
          Export watermark forensic decoder
        </h2>
        <div className="bg-white border border-border p-5">
          <p className="text-xs text-muted-foreground mb-3">
            Every CSV export embeds an invisible watermark in each row's Notes field. If a competitor publishes
            or leaks your data, paste <span className="font-bold">any one row</span> (or even just the Notes cell)
            below — we'll identify who exported it, when, and from which IP.
          </p>
          <textarea
            value={decodeInput}
            onChange={(e) => setDecodeInput(e.target.value)}
            data-testid="watermark-input"
            placeholder="Paste the suspect row, cell value, or pasted snippet here…"
            rows={4}
            className="w-full px-3 py-2 border border-border rounded-sm bg-white text-sm font-mono focus:border-primary focus:outline-none"
          />
          <div className="flex items-center gap-2 mt-2">
            <button onClick={decode} disabled={decoding}
              data-testid="watermark-decode"
              className="px-4 py-2 text-sm font-semibold bg-primary text-primary-foreground hover:bg-[#002277] rounded-sm disabled:opacity-50 flex items-center gap-2">
              <Fingerprint size={14} /> {decoding ? "Decoding…" : "Decode"}
            </button>
            {decodeInput && (
              <button onClick={() => { setDecodeInput(""); setDecodeResult(null); }}
                data-testid="watermark-clear"
                className="px-4 py-2 text-sm font-semibold border border-border hover:bg-secondary rounded-sm">
                Clear
              </button>
            )}
          </div>

          {decodeResult && (
            <div className="mt-4 border border-border bg-secondary p-4" data-testid="watermark-result">
              {!decodeResult.found ? (
                <div className="text-sm">
                  <div className="flex items-center gap-2 mb-2" style={{ color: "#FF9800" }}>
                    <Warning size={16} weight="bold" />
                    <span className="font-bold uppercase tracking-[0.1em] text-xs">No watermark detected</span>
                  </div>
                  <p className="text-xs text-muted-foreground">{decodeResult.message}</p>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2 mb-3" style={{ color: "#008A00" }}>
                    <CheckCircle size={16} weight="bold" />
                    <span className="font-bold uppercase tracking-[0.1em] text-xs">Watermark identified</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                    <Row label="Exporter" value={
                      decodeResult.exporter
                        ? `${decodeResult.exporter.name} (${decodeResult.exporter.email})`
                        : `unknown · hash ${decodeResult.user_hash}`
                    } />
                    <Row label="Role" value={decodeResult.exporter?.role || "—"} />
                    <Row label="Exported at" value={new Date(decodeResult.exported_at_ms).toLocaleString()} />
                    <Row label="From IP" value={decodeResult.audit_event?.ip || "—"} mono />
                    <Row label="Rows in export" value={decodeResult.audit_event?.row_count ?? "—"} />
                    <Row label="Filters used" value={JSON.stringify(decodeResult.audit_event?.filters || {})} mono />
                    <Row label="Forensic ID" value={decodeResult.opaque} mono />
                    <Row label="User agent" value={decodeResult.audit_event?.ua || "—"} mono small />
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="px-8 pt-6 flex flex-wrap gap-2 items-center">
        <input
          placeholder="Filter by email"
          value={filter.email}
          onChange={(e) => setFilter({ ...filter, email: e.target.value })}
          data-testid="security-filter-email"
          className="text-sm border border-border px-3 py-2 rounded-sm bg-white focus:border-primary focus:outline-none w-56"
        />
        <select
          value={filter.action}
          onChange={(e) => setFilter({ ...filter, action: e.target.value })}
          data-testid="security-filter-action"
          className="text-sm border border-border px-3 py-2 rounded-sm bg-white focus:border-primary focus:outline-none"
        >
          <option value="">All actions</option>
          {Object.entries(ACTION_LABELS).map(([id, m]) => (
            <option key={id} value={id}>{m.label}</option>
          ))}
        </select>
        <select
          value={filter.days}
          onChange={(e) => setFilter({ ...filter, days: parseInt(e.target.value, 10) })}
          data-testid="security-filter-days"
          className="text-sm border border-border px-3 py-2 rounded-sm bg-white focus:border-primary focus:outline-none"
        >
          <option value="1">Last 24h</option>
          <option value="7">Last 7 days</option>
          <option value="30">Last 30 days</option>
          <option value="90">Last 90 days</option>
        </select>
        <span className="ml-auto text-xs tabular-nums text-muted-foreground" data-testid="security-count">
          {events.length} event{events.length !== 1 && "s"}
        </span>
      </div>

      {/* Audit table */}
      <div className="px-8 py-6">
        <div className="bg-white border border-border">
          <table className="w-full text-sm" data-testid="security-audit-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.15em] text-muted-foreground border-b border-border">
                <th className="px-4 py-3 font-bold">When</th>
                <th className="px-4 py-3 font-bold">Action</th>
                <th className="px-4 py-3 font-bold">Who</th>
                <th className="px-4 py-3 font-bold">IP</th>
                <th className="px-4 py-3 font-bold">Details</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="px-4 py-12 text-center text-sm text-muted-foreground">Loading…</td></tr>
              ) : events.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-16 text-center text-sm text-muted-foreground">
                  <CheckCircle size={32} weight="bold" className="mx-auto mb-3" style={{ color: "#008A00" }} />
                  No matching events. Clean slate.
                </td></tr>
              ) : events.map((e, i) => {
                const meta = ACTION_LABELS[e.action] || { label: e.action, color: "#52525B" };
                return (
                  <tr key={i} className="border-b border-border last:border-0 hover:bg-secondary/40"
                    data-testid={`audit-row-${i}`}>
                    <td className="px-4 py-2.5 text-xs tabular-nums text-muted-foreground whitespace-nowrap">
                      {fmtDate(e.at)}
                    </td>
                    <td className="px-4 py-2.5">
                      <span className="text-xs uppercase tracking-[0.1em] font-bold" style={{ color: meta.color }}>
                        {meta.label}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-xs">
                      {e.actor_email || "—"}
                      {e.actor_role && <span className="ml-1 text-[10px] uppercase tracking-[0.15em] text-muted-foreground">({e.actor_role})</span>}
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs">{e.ip || "—"}</td>
                    <td className="px-4 py-2.5 text-xs text-muted-foreground max-w-[280px] truncate" title={JSON.stringify(e.meta || {})}>
                      {Object.keys(e.meta || {}).length > 0 ? JSON.stringify(e.meta) : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, color, icon: Icon, testid }) {
  return (
    <div className="border border-border bg-white p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold">{label}</div>
        {Icon && <Icon size={16} weight="bold" style={{ color }} />}
      </div>
      <div className="font-display text-3xl font-bold tabular-nums" style={{ color }}>{value}</div>
    </div>
  );
}

function Row({ label, value, mono, small }) {
  return (
    <div className="border-b border-border pb-1.5">
      <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-bold">{label}</div>
      <div className={`${mono ? "font-mono" : ""} ${small ? "text-xs" : "text-sm"} mt-0.5 break-all`}>{value}</div>
    </div>
  );
}
